import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import PageHero from '@/components/shared/PageHero';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { publicApi } from '@/lib/api';
import useSEO from '@/hooks/useSEO';

/**
 * Public Partners page — plain, responsive grid of every partner logo.
 * No descriptive body copy per client brief.
 */
export default function PartnersPage() {
    const [items, setItems] = useState(null); // null = loading, [] = empty

    useSEO({
        title: 'Partners',
        description:
            'The corporates, government bodies and community organisations partnering with VVVSI Trust across employment, education, health and social welfare initiatives.',
    });

    useEffect(() => {
        publicApi
            .getPartners()
            .then((data) => setItems(Array.isArray(data) ? data : []))
            .catch(() => setItems([]));
    }, []);

    const loading = items === null;
    const visible = (items || []).filter(
        (p) => (p.logo_url || '').trim() !== '',
    );

    return (
        <div data-testid="partners-page">
            <PageHero
                eyebrow="Partners"
                title="Working alongside those who believe in the mission."
                subtitle="Corporates, government bodies and community organisations that back our impact."
            />
            <section className="section-pad">
                <div className="container-page">
                    {loading ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                            {Array.from({ length: 15 }).map((_, i) => (
                                <Skeleton
                                    key={i}
                                    className="aspect-[4/3] w-full rounded-lg"
                                />
                            ))}
                        </div>
                    ) : visible.length === 0 ? (
                        <div className="py-16 text-center text-sm text-muted-foreground">
                            No partner logos published yet.
                        </div>
                    ) : (
                        <div
                            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4"
                            data-testid="partners-grid"
                        >
                            {visible.map((p, i) => (
                                <motion.div
                                    key={p.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{
                                        duration: 0.35,
                                        delay: Math.min(i * 0.03, 0.45),
                                    }}
                                    viewport={{ once: true, margin: '-40px' }}
                                >
                                    <Card
                                        className="bg-card border-border overflow-hidden card-lift"
                                        data-testid={`partner-card-${p.id}`}
                                    >
                                        <CardContent className="p-0">
                                            <div className="aspect-[4/3] bg-white flex items-center justify-center p-4">
                                                <img
                                                    src={p.logo_url}
                                                    alt=""
                                                    loading="lazy"
                                                    className="max-h-full max-w-full object-contain"
                                                    onError={(e) => {
                                                        e.currentTarget.style.display =
                                                            'none';
                                                    }}
                                                />
                                            </div>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
